export const statisticsCourse = {
  id: 'statistics',
  title: 'Statistics',
  description: 'Comprehensive introduction to statistical methods, probability theory, data analysis, and statistical inference for scientific and business applications.',
  status: 'published',
  duration: '15 weeks',
  level: 'Intermediate',
  credits: 2,
  prerequisites: ['Calculus I', 'Mathematical Foundations'],
  learningObjectives: [
    'Master descriptive statistics and data visualization techniques',
    'Understand probability distributions and statistical inference',
    'Apply hypothesis testing and confidence interval methods',
    'Perform regression analysis and correlation studies',
    'Use statistical software for data analysis',
    'Interpret statistical results in real-world contexts'
  ],
  modules: [
    {
      id: 'descriptive-statistics',
      title: 'Descriptive Statistics and Data Analysis',
      description: 'Introduction to statistical measures, data visualization, and exploratory data analysis techniques.',
      duration: '4 weeks',
      lessons: [
        {
          id: 'data-basics',
          title: 'Data Types and Descriptive Measures',
          content: `
# Data Types and Descriptive Measures

## Learning Objectives
- Identify different types of data and measurement scales
- Calculate measures of central tendency and variability
- Create and interpret data visualizations
- Understand distribution shapes and outliers

## Types of Data

### Qualitative vs Quantitative
- **Qualitative (Categorical)**: Non-numerical data
  - Nominal: No natural order (colors, names, categories)
  - Ordinal: Natural order exists (ratings, grades, rankings)
  
- **Quantitative (Numerical)**: Numerical data
  - Discrete: Countable values (number of students, cars)
  - Continuous: Measurable values (height, weight, time)

### Measurement Scales
1. **Nominal Scale**: Categories with no order
   - Examples: Gender, race, political affiliation
   - Operations: Counting, mode

2. **Ordinal Scale**: Categories with meaningful order
   - Examples: Letter grades, survey ratings (poor/fair/good/excellent)
   - Operations: Counting, mode, median, percentiles

3. **Interval Scale**: Equal intervals, no true zero
   - Examples: Temperature (°F, °C), calendar years
   - Operations: All ordinal operations plus mean, standard deviation

4. **Ratio Scale**: Equal intervals with true zero
   - Examples: Height, weight, income, age
   - Operations: All interval operations plus ratios, geometric mean

## Measures of Central Tendency

### Mean (Arithmetic Average)
**Sample Mean**: x̄ = (Σxᵢ) / n
**Population Mean**: μ = (Σxᵢ) / N

Properties:
- Sensitive to outliers
- Balancing point of distribution
- Most common measure for interval/ratio data

### Median
The middle value when data is ordered
- For odd n: median = x((n+1)/2)
- For even n: median = (x(n/2) + x(n/2+1)) / 2

Properties:
- Resistant to outliers
- Best for skewed distributions
- Used with ordinal, interval, and ratio data

### Mode
The most frequently occurring value

Properties:
- Can be used with all data types
- May have no mode, one mode, or multiple modes
- Not affected by outliers

### Relationship Between Mean, Median, Mode
- **Symmetric distribution**: Mean ≈ Median ≈ Mode
- **Right-skewed**: Mean > Median > Mode
- **Left-skewed**: Mode > Median > Mean

## Measures of Variability

### Range
Range = Maximum - Minimum
- Simple but sensitive to outliers
- Useful for quick assessment

### Interquartile Range (IQR)
IQR = Q₃ - Q₁
- Q₁ (25th percentile), Q₃ (75th percentile)
- Resistant to outliers
- Used in box plots

### Variance
**Sample Variance**: s² = Σ(xᵢ - x̄)² / (n-1)
**Population Variance**: σ² = Σ(xᵢ - μ)² / N

### Standard Deviation
**Sample**: s = √s²
**Population**: σ = √σ²

Properties:
- Same units as original data
- Average distance from mean
- Approximately 68% of data within 1 SD of mean (normal distribution)

### Coefficient of Variation
CV = (Standard Deviation / Mean) × 100%
- Unitless measure of relative variability
- Useful for comparing variability between different datasets

## Percentiles and Quartiles

### Percentiles
The kth percentile is the value below which k% of the data falls

### Quartiles
- Q₁: 25th percentile
- Q₂: 50th percentile (median)
- Q₃: 75th percentile

### Five-Number Summary
1. Minimum
2. Q₁
3. Median (Q₂)
4. Q₃
5. Maximum

## Data Visualization

### Histograms
- Show distribution shape
- Bin width affects appearance
- Use for continuous data

### Box Plots
- Display five-number summary
- Show outliers (beyond 1.5 × IQR from quartiles)
- Compare multiple groups

### Scatter Plots
- Show relationship between two variables
- Identify correlation patterns
- Detect outliers and non-linear relationships

### Bar Charts
- Categorical data display
- Compare frequencies or proportions
- Horizontal or vertical orientation

### Pie Charts
- Show parts of a whole
- Best for fewer than 7 categories
- Use when percentages are important

## Outlier Detection

### IQR Method
- Lower fence: Q₁ - 1.5 × IQR
- Upper fence: Q₃ + 1.5 × IQR
- Values outside fences are potential outliers

### Z-Score Method
z = (x - μ) / σ
- Values with |z| > 2 or 3 are potential outliers

### Modified Z-Score
Uses median and MAD (Median Absolute Deviation)
- More robust than standard z-score

## Distribution Shapes

### Symmetry
- Symmetric: Mirror image around center
- Left-skewed: Tail extends to the left
- Right-skewed: Tail extends to the right

### Kurtosis
- Mesokurtic: Normal distribution shape
- Leptokurtic: Peaked distribution
- Platykurtic: Flat distribution

### Modality
- Unimodal: One peak
- Bimodal: Two peaks
- Multimodal: Multiple peaks
- Uniform: No clear peaks

## Applications
- Quality control in manufacturing
- Financial data analysis
- Medical research and epidemiology
- Market research and consumer behavior
- Educational assessment and testing
          `,
          interactiveExercises: [
            {
              id: 'statistical-calculator',
              title: 'Statistical Analysis Calculator',
              description: 'Calculate descriptive statistics and create visualizations',
              initialCode: `class StatisticalAnalyzer {
    constructor(data = []) {
        this.data = [...data].sort((a, b) => a - b);
    }
    
    // Measures of Central Tendency
    mean() {
        // Calculate arithmetic mean
        return 0;
    }
    
    median() {
        // Calculate median value
        return 0;
    }
    
    mode() {
        // Find mode(s) in the dataset
        return [];
    }
    
    // Measures of Variability
    range() {
        // Calculate range (max - min)
        return 0;
    }
    
    variance(isPopulation = false) {
        // Calculate variance (sample or population)
        return 0;
    }
    
    standardDeviation(isPopulation = false) {
        // Calculate standard deviation
        return 0;
    }
    
    interquartileRange() {
        // Calculate IQR (Q3 - Q1)
        return 0;
    }
    
    // Percentiles and Quartiles
    percentile(k) {
        // Calculate kth percentile
        return 0;
    }
    
    quartiles() {
        // Calculate Q1, Q2 (median), Q3
        return { q1: 0, q2: 0, q3: 0 };
    }
    
    fiveNumberSummary() {
        // Calculate min, Q1, median, Q3, max
        return { min: 0, q1: 0, median: 0, q3: 0, max: 0 };
    }
    
    // Outlier Detection
    detectOutliers(method = 'iqr') {
        // Detect outliers using IQR or z-score method
        return [];
    }
    
    zScores() {
        // Calculate z-scores for all data points
        return [];
    }
    
    // Distribution Analysis
    skewness() {
        // Calculate skewness measure
        return 0;
    }
    
    kurtosis() {
        // Calculate kurtosis measure
        return 0;
    }
    
    // Data Visualization Helper
    createHistogram(bins = 10) {
        // Create histogram data structure
        return [];
    }
    
    boxPlotData() {
        // Prepare data for box plot
        return {};
    }
    
    // Summary Report
    generateReport() {
        return {
            centralTendency: {
                mean: this.mean(),
                median: this.median(),
                mode: this.mode()
            },
            variability: {
                range: this.range(),
                variance: this.variance(),
                standardDeviation: this.standardDeviation(),
                iqr: this.interquartileRange()
            },
            distribution: {
                skewness: this.skewness(),
                kurtosis: this.kurtosis(),
                outliers: this.detectOutliers()
            },
            fiveNumber: this.fiveNumberSummary()
        };
    }
}

// Test data
const testData = [12, 15, 18, 20, 22, 25, 28, 30, 35, 40, 45, 50, 55, 60, 65];
const analyzer = new StatisticalAnalyzer(testData);

console.log("Dataset:", testData);
console.log("Mean:", analyzer.mean());
console.log("Median:", analyzer.median());
console.log("Mode:", analyzer.mode());
console.log("Standard Deviation:", analyzer.standardDeviation());
console.log("Five Number Summary:", analyzer.fiveNumberSummary());
console.log("Full Report:", analyzer.generateReport());`,
              solution: `class StatisticalAnalyzer {
    constructor(data = []) {
        this.data = [...data].sort((a, b) => a - b);
    }
    
    mean() {
        if (this.data.length === 0) return 0;
        return this.data.reduce((sum, value) => sum + value, 0) / this.data.length;
    }
    
    median() {
        if (this.data.length === 0) return 0;
        const n = this.data.length;
        if (n % 2 === 0) {
            return (this.data[n/2 - 1] + this.data[n/2]) / 2;
        } else {
            return this.data[Math.floor(n/2)];
        }
    }
    
    mode() {
        if (this.data.length === 0) return [];
        
        const frequency = {};
        let maxFreq = 0;
        
        // Count frequencies
        this.data.forEach(value => {
            frequency[value] = (frequency[value] || 0) + 1;
            maxFreq = Math.max(maxFreq, frequency[value]);
        });
        
        // Find all modes
        const modes = [];
        for (let value in frequency) {
            if (frequency[value] === maxFreq && maxFreq > 1) {
                modes.push(parseFloat(value));
            }
        }
        
        return modes.sort((a, b) => a - b);
    }
    
    range() {
        if (this.data.length === 0) return 0;
        return Math.max(...this.data) - Math.min(...this.data);
    }
    
    variance(isPopulation = false) {
        if (this.data.length === 0) return 0;
        const mean = this.mean();
        const squaredDiffs = this.data.map(value => Math.pow(value - mean, 2));
        const sumSquaredDiffs = squaredDiffs.reduce((sum, diff) => sum + diff, 0);
        
        const denominator = isPopulation ? this.data.length : this.data.length - 1;
        return sumSquaredDiffs / denominator;
    }
    
    standardDeviation(isPopulation = false) {
        return Math.sqrt(this.variance(isPopulation));
    }
    
    percentile(k) {
        if (this.data.length === 0) return 0;
        if (k < 0 || k > 100) throw new Error("Percentile must be between 0 and 100");
        
        const index = (k / 100) * (this.data.length - 1);
        const lower = Math.floor(index);
        const upper = Math.ceil(index);
        
        if (lower === upper) {
            return this.data[lower];
        } else {
            const weight = index - lower;
            return this.data[lower] * (1 - weight) + this.data[upper] * weight;
        }
    }
    
    quartiles() {
        return {
            q1: this.percentile(25),
            q2: this.percentile(50), // median
            q3: this.percentile(75)
        };
    }
    
    interquartileRange() {
        const q = this.quartiles();
        return q.q3 - q.q1;
    }
    
    fiveNumberSummary() {
        const q = this.quartiles();
        return {
            min: Math.min(...this.data),
            q1: q.q1,
            median: q.q2,
            q3: q.q3,
            max: Math.max(...this.data)
        };
    }
    
    detectOutliers(method = 'iqr') {
        if (method === 'iqr') {
            const q = this.quartiles();
            const iqr = q.q3 - q.q1;
            const lowerFence = q.q1 - 1.5 * iqr;
            const upperFence = q.q3 + 1.5 * iqr;
            
            return this.data.filter(value => value < lowerFence || value > upperFence);
        } else if (method === 'zscore') {
            const mean = this.mean();
            const sd = this.standardDeviation();
            
            return this.data.filter(value => Math.abs((value - mean) / sd) > 2);
        }
        
        return [];
    }
    
    zScores() {
        const mean = this.mean();
        const sd = this.standardDeviation();
        
        return this.data.map(value => (value - mean) / sd);
    }
    
    skewness() {
        if (this.data.length < 3) return 0;
        
        const mean = this.mean();
        const sd = this.standardDeviation();
        const n = this.data.length;
        
        const skewnessSum = this.data.reduce((sum, value) => {
            return sum + Math.pow((value - mean) / sd, 3);
        }, 0);
        
        return (n / ((n - 1) * (n - 2))) * skewnessSum;
    }
    
    kurtosis() {
        if (this.data.length < 4) return 0;
        
        const mean = this.mean();
        const sd = this.standardDeviation();
        const n = this.data.length;
        
        const kurtosisSum = this.data.reduce((sum, value) => {
            return sum + Math.pow((value - mean) / sd, 4);
        }, 0);
        
        const kurtosisValue = (n * (n + 1) / ((n - 1) * (n - 2) * (n - 3))) * kurtosisSum;
        const correction = (3 * Math.pow(n - 1, 2)) / ((n - 2) * (n - 3));
        
        return kurtosisValue - correction; // Excess kurtosis
    }
    
    createHistogram(bins = 10) {
        if (this.data.length === 0) return [];
        
        const min = Math.min(...this.data);
        const max = Math.max(...this.data);
        const binWidth = (max - min) / bins;
        
        const histogram = [];
        for (let i = 0; i < bins; i++) {
            const binStart = min + i * binWidth;
            const binEnd = binStart + binWidth;
            const count = this.data.filter(value => 
                value >= binStart && (i === bins - 1 ? value <= binEnd : value < binEnd)
            ).length;
            
            histogram.push({
                binStart: Math.round(binStart * 100) / 100,
                binEnd: Math.round(binEnd * 100) / 100,
                count: count,
                frequency: count / this.data.length
            });
        }
        
        return histogram;
    }
    
    boxPlotData() {
        const summary = this.fiveNumberSummary();
        const outliers = this.detectOutliers('iqr');
        
        return {
            ...summary,
            outliers: outliers,
            iqr: summary.q3 - summary.q1
        };
    }
    
    generateReport() {
        const centralTendency = {
            mean: Math.round(this.mean() * 1000) / 1000,
            median: Math.round(this.median() * 1000) / 1000,
            mode: this.mode()
        };
        
        const variability = {
            range: Math.round(this.range() * 1000) / 1000,
            variance: Math.round(this.variance() * 1000) / 1000,
            standardDeviation: Math.round(this.standardDeviation() * 1000) / 1000,
            iqr: Math.round(this.interquartileRange() * 1000) / 1000,
            coefficientOfVariation: Math.round((this.standardDeviation() / this.mean()) * 100 * 1000) / 1000
        };
        
        const distribution = {
            skewness: Math.round(this.skewness() * 1000) / 1000,
            kurtosis: Math.round(this.kurtosis() * 1000) / 1000,
            outliers: this.detectOutliers(),
            shape: this.getDistributionShape()
        };
        
        return {
            dataSize: this.data.length,
            centralTendency,
            variability,
            distribution,
            fiveNumber: this.fiveNumberSummary(),
            histogram: this.createHistogram(),
            boxPlot: this.boxPlotData()
        };
    }
    
    getDistributionShape() {
        const skew = this.skewness();
        const kurt = this.kurtosis();
        
        let shape = [];
        
        // Skewness interpretation
        if (Math.abs(skew) < 0.5) {
            shape.push("approximately symmetric");
        } else if (skew > 0.5) {
            shape.push("right-skewed");
        } else {
            shape.push("left-skewed");
        }
        
        // Kurtosis interpretation
        if (Math.abs(kurt) < 0.5) {
            shape.push("mesokurtic (normal-like peakedness)");
        } else if (kurt > 0.5) {
            shape.push("leptokurtic (peaked)");
        } else {
            shape.push("platykurtic (flat)");
        }
        
        return shape.join(", ");
    }
}`,
              hints: [
                'Mean is sum divided by count',
                'Median is middle value of sorted data',
                'Variance measures spread from mean',
                'Quartiles divide data into four equal parts',
                'Outliers are values far from typical range'
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'probability',
      title: 'Probability Theory and Distributions',
      description: 'Fundamental probability concepts, random variables, and probability distributions.',
      duration: '4 weeks',
      lessons: [
        {
          id: 'probability-basics',
          title: 'Probability Fundamentals',
          content: `
# Probability Fundamentals

## Learning Objectives
- Understand basic probability concepts and rules
- Calculate probabilities using different approaches
- Work with conditional probability and independence
- Apply Bayes' theorem to real-world problems

## Basic Probability Concepts

### Sample Space and Events
- **Sample Space (S)**: Set of all possible outcomes
- **Event (E)**: Subset of sample space
- **Simple Event**: Single outcome
- **Compound Event**: Multiple outcomes

### Probability Rules
1. **Range**: 0 ≤ P(E) ≤ 1
2. **Certainty**: P(S) = 1
3. **Impossibility**: P(∅) = 0
4. **Complement**: P(E') = 1 - P(E)

### Approaches to Probability

#### Classical (Theoretical) Probability
P(E) = Number of favorable outcomes / Total number of equally likely outcomes

Example: Rolling a fair die
P(getting a 3) = 1/6

#### Empirical (Relative Frequency) Probability
P(E) = Number of times E occurred / Total number of trials

Example: Flip coin 1000 times, get 523 heads
P(heads) ≈ 523/1000 = 0.523

#### Subjective Probability
Based on personal judgment, experience, or beliefs
Example: "There's a 70% chance it will rain tomorrow"

## Probability Rules and Operations

### Addition Rule
**For mutually exclusive events**: P(A ∪ B) = P(A) + P(B)
**For general events**: P(A ∪ B) = P(A) + P(B) - P(A ∩ B)

### Multiplication Rule
**For independent events**: P(A ∩ B) = P(A) × P(B)
**For general events**: P(A ∩ B) = P(A) × P(B|A)

### Conditional Probability
P(B|A) = P(A ∩ B) / P(A), provided P(A) > 0

The probability of B occurring given that A has occurred.

### Independence
Events A and B are independent if:
- P(A ∩ B) = P(A) × P(B)
- P(A|B) = P(A)
- P(B|A) = P(B)

## Bayes' Theorem

### Formula
P(A|B) = [P(B|A) × P(A)] / P(B)

Where:
- P(A|B): Posterior probability
- P(B|A): Likelihood
- P(A): Prior probability
- P(B): Marginal probability

### Extended Form
P(A|B) = [P(B|A) × P(A)] / [P(B|A) × P(A) + P(B|A') × P(A')]

### Applications
- Medical diagnosis
- Spam filtering
- Quality control
- Machine learning

## Counting Principles

### Fundamental Counting Principle
If task 1 can be done in n₁ ways and task 2 in n₂ ways, then both tasks can be done in n₁ × n₂ ways.

### Permutations
**Without repetition**: P(n,r) = n! / (n-r)!
**With repetition**: nʳ

### Combinations
**Without repetition**: C(n,r) = n! / [r!(n-r)!]
**With repetition**: C(n+r-1,r)

## Common Probability Distributions

### Discrete Uniform Distribution
All outcomes equally likely
P(X = x) = 1/n for each outcome

### Binomial Distribution
Fixed number of independent trials, two outcomes
P(X = k) = C(n,k) × p^k × (1-p)^(n-k)

Parameters: n (trials), p (success probability)

### Poisson Distribution
Rare events over fixed interval
P(X = k) = (λ^k × e^(-λ)) / k!

Parameter: λ (average rate)

### Geometric Distribution
Number of trials until first success
P(X = k) = (1-p)^(k-1) × p

Parameter: p (success probability)

## Continuous Distributions

### Uniform Distribution
Constant probability over interval [a,b]
f(x) = 1/(b-a) for a ≤ x ≤ b

### Normal Distribution
Bell-shaped, symmetric distribution
f(x) = (1/σ√(2π)) × e^(-(x-μ)²/(2σ²))

Parameters: μ (mean), σ (standard deviation)

### Standard Normal Distribution
Normal distribution with μ = 0, σ = 1
Z = (X - μ)/σ standardizes any normal distribution

### Exponential Distribution
Models time between events
f(x) = λe^(-λx) for x ≥ 0

Parameter: λ (rate parameter)

## Expected Value and Variance

### Expected Value (Mean)
**Discrete**: E[X] = Σ x × P(X = x)
**Continuous**: E[X] = ∫ x × f(x) dx

### Variance
Var(X) = E[X²] - (E[X])²

### Standard Deviation
SD(X) = √Var(X)

### Properties
- E[aX + b] = aE[X] + b
- Var(aX + b) = a²Var(X)
- If X and Y are independent: E[X + Y] = E[X] + E[Y]

## Applications
- Risk assessment and insurance
- Quality control in manufacturing
- Finance and investment analysis
- Genetics and medical research
- Game theory and decision making
          `,
          interactiveExercises: [
            {
              id: 'probability-calculator',
              title: 'Probability Calculator and Simulator',
              description: 'Calculate probabilities and simulate random events',
              initialCode: `class ProbabilityCalculator {
    // Basic probability calculations
    static classicalProbability(favorableOutcomes, totalOutcomes) {
        // Calculate classical probability
        return 0;
    }
    
    static empiricalProbability(successes, trials) {
        // Calculate empirical probability
        return 0;
    }
    
    // Conditional probability and independence
    static conditionalProbability(jointAB, probabilityA) {
        // Calculate P(B|A) = P(A∩B) / P(A)
        return 0;
    }
    
    static areIndependent(probabilityA, probabilityB, jointAB) {
        // Check if events A and B are independent
        return false;
    }
    
    // Bayes' theorem
    static bayesTheorem(priorA, likelihoodBA, likelihoodBNotA) {
        // Calculate posterior probability using Bayes' theorem
        return 0;
    }
    
    // Counting methods
    static permutation(n, r) {
        // Calculate P(n,r) = n! / (n-r)!
        return 0;
    }
    
    static combination(n, r) {
        // Calculate C(n,r) = n! / (r!(n-r)!)
        return 0;
    }
    
    static factorial(n) {
        // Calculate n!
        return 0;
    }
    
    // Discrete distributions
    static binomialProbability(n, k, p) {
        // P(X = k) in binomial distribution
        return 0;
    }
    
    static poissonProbability(lambda, k) {
        // P(X = k) in Poisson distribution
        return 0;
    }
    
    static geometricProbability(p, k) {
        // P(X = k) in geometric distribution
        return 0;
    }
    
    // Normal distribution
    static standardNormalCDF(z) {
        // Approximate standard normal CDF using error function
        return 0;
    }
    
    static normalProbability(x, mean, standardDeviation) {
        // Calculate probability for normal distribution
        return 0;
    }
    
    // Simulation methods
    static simulateCoinFlips(numFlips) {
        // Simulate coin flips and return results
        return [];
    }
    
    static simulateDiceRolls(numRolls, numSides = 6) {
        // Simulate dice rolls
        return [];
    }
    
    static simulateBinomial(n, p, numTrials) {
        // Simulate binomial random variables
        return [];
    }
    
    static monteCarloEstimate(experiment, numTrials) {
        // Use Monte Carlo method to estimate probability
        return 0;
    }
}

// Test the calculator
console.log("Classical probability (fair die, getting 3):", 
    ProbabilityCalculator.classicalProbability(1, 6));

console.log("Binomial probability (n=10, k=3, p=0.3):", 
    ProbabilityCalculator.binomialProbability(10, 3, 0.3));

console.log("Coin flip simulation (100 flips):", 
    ProbabilityCalculator.simulateCoinFlips(100));

// Bayes' theorem example: Medical test
const prior = 0.01; // 1% of population has disease
const sensitivityPositive = 0.95; // P(test+|disease)
const specificityNegative = 0.02; // P(test+|no disease)

console.log("Posterior probability (Bayes):", 
    ProbabilityCalculator.bayesTheorem(prior, sensitivityPositive, specificityNegative));`,
              solution: `class ProbabilityCalculator {
    static classicalProbability(favorableOutcomes, totalOutcomes) {
        if (totalOutcomes <= 0) throw new Error("Total outcomes must be positive");
        return favorableOutcomes / totalOutcomes;
    }
    
    static empiricalProbability(successes, trials) {
        if (trials <= 0) throw new Error("Number of trials must be positive");
        return successes / trials;
    }
    
    static conditionalProbability(jointAB, probabilityA) {
        if (probabilityA <= 0) throw new Error("P(A) must be positive");
        return jointAB / probabilityA;
    }
    
    static areIndependent(probabilityA, probabilityB, jointAB) {
        const tolerance = 1e-10;
        return Math.abs(jointAB - (probabilityA * probabilityB)) < tolerance;
    }
    
    static bayesTheorem(priorA, likelihoodBA, likelihoodBNotA) {
        const priorNotA = 1 - priorA;
        const marginalB = likelihoodBA * priorA + likelihoodBNotA * priorNotA;
        
        if (marginalB === 0) throw new Error("Marginal probability cannot be zero");
        
        return (likelihoodBA * priorA) / marginalB;
    }
    
    static factorial(n) {
        if (n < 0) throw new Error("Factorial is not defined for negative numbers");
        if (n === 0 || n === 1) return 1;
        
        let result = 1;
        for (let i = 2; i <= n; i++) {
            result *= i;
        }
        return result;
    }
    
    static permutation(n, r) {
        if (r > n || r < 0) throw new Error("Invalid permutation parameters");
        return this.factorial(n) / this.factorial(n - r);
    }
    
    static combination(n, r) {
        if (r > n || r < 0) throw new Error("Invalid combination parameters");
        return this.factorial(n) / (this.factorial(r) * this.factorial(n - r));
    }
    
    static binomialProbability(n, k, p) {
        if (k > n || k < 0) throw new Error("Invalid binomial parameters");
        if (p < 0 || p > 1) throw new Error("Probability must be between 0 and 1");
        
        const combination = this.combination(n, k);
        return combination * Math.pow(p, k) * Math.pow(1 - p, n - k);
    }
    
    static poissonProbability(lambda, k) {
        if (lambda <= 0) throw new Error("Lambda must be positive");
        if (k < 0) throw new Error("k must be non-negative");
        
        return (Math.pow(lambda, k) * Math.exp(-lambda)) / this.factorial(k);
    }
    
    static geometricProbability(p, k) {
        if (p <= 0 || p >= 1) throw new Error("Probability must be between 0 and 1");
        if (k <= 0) throw new Error("k must be positive");
        
        return Math.pow(1 - p, k - 1) * p;
    }
    
    static standardNormalCDF(z) {
        // Approximation using error function
        const a1 =  0.254829592;
        const a2 = -0.284496736;
        const a3 =  1.421413741;
        const a4 = -1.453152027;
        const a5 =  1.061405429;
        const p  =  0.3275911;
        
        const sign = z < 0 ? -1 : 1;
        z = Math.abs(z) / Math.sqrt(2.0);
        
        const t = 1.0 / (1.0 + p * z);
        const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-z * z);
        
        return 0.5 * (1.0 + sign * y);
    }
    
    static normalProbability(x, mean, standardDeviation) {
        const z = (x - mean) / standardDeviation;
        return this.standardNormalCDF(z);
    }
    
    static simulateCoinFlips(numFlips) {
        const results = [];
        for (let i = 0; i < numFlips; i++) {
            results.push(Math.random() < 0.5 ? 'H' : 'T');
        }
        
        const heads = results.filter(r => r === 'H').length;
        return {
            results: results,
            heads: heads,
            tails: numFlips - heads,
            headsProportion: heads / numFlips
        };
    }
    
    static simulateDiceRolls(numRolls, numSides = 6) {
        const results = [];
        const frequencies = {};
        
        for (let i = 1; i <= numSides; i++) {
            frequencies[i] = 0;
        }
        
        for (let i = 0; i < numRolls; i++) {
            const roll = Math.floor(Math.random() * numSides) + 1;
            results.push(roll);
            frequencies[roll]++;
        }
        
        return {
            results: results,
            frequencies: frequencies,
            proportions: Object.fromEntries(
                Object.entries(frequencies).map(([k, v]) => [k, v / numRolls])
            )
        };
    }
    
    static simulateBinomial(n, p, numTrials) {
        const results = [];
        
        for (let trial = 0; trial < numTrials; trial++) {
            let successes = 0;
            for (let i = 0; i < n; i++) {
                if (Math.random() < p) {
                    successes++;
                }
            }
            results.push(successes);
        }
        
        return results;
    }
    
    static monteCarloEstimate(experiment, numTrials) {
        let successes = 0;
        
        for (let i = 0; i < numTrials; i++) {
            if (experiment()) {
                successes++;
            }
        }
        
        return successes / numTrials;
    }
    
    // Additional utility methods
    static expectedValue(values, probabilities) {
        if (values.length !== probabilities.length) {
            throw new Error("Values and probabilities arrays must have same length");
        }
        
        return values.reduce((sum, value, index) => sum + value * probabilities[index], 0);
    }
    
    static variance(values, probabilities) {
        const mean = this.expectedValue(values, probabilities);
        const expectedSquare = values.reduce((sum, value, index) => 
            sum + value * value * probabilities[index], 0);
        
        return expectedSquare - mean * mean;
    }
    
    static standardDeviation(values, probabilities) {
        return Math.sqrt(this.variance(values, probabilities));
    }
}

// Enhanced testing and examples
console.log("=== Basic Probability Tests ===");
console.log("Classical probability (fair die, getting 3):", 
    ProbabilityCalculator.classicalProbability(1, 6));

console.log("\\n=== Discrete Distributions ===");
console.log("Binomial probability (n=10, k=3, p=0.3):", 
    ProbabilityCalculator.binomialProbability(10, 3, 0.3));

console.log("Poisson probability (λ=2, k=3):", 
    ProbabilityCalculator.poissonProbability(2, 3));

console.log("Geometric probability (p=0.2, k=5):", 
    ProbabilityCalculator.geometricProbability(0.2, 5));

console.log("\\n=== Simulations ===");
const coinSim = ProbabilityCalculator.simulateCoinFlips(1000);
console.log(\`Coin simulation (1000 flips): \${coinSim.heads} heads (\${(coinSim.headsProportion * 100).toFixed(1)}%)\`);

const diceSim = ProbabilityCalculator.simulateDiceRolls(600, 6);
console.log("Dice simulation (600 rolls):", diceSim.proportions);

console.log("\\n=== Bayes' Theorem Example ===");
// Medical test example
const diseasePrevalence = 0.01; // 1% of population has disease
const testSensitivity = 0.95; // P(test+|disease) = 95%
const testSpecificity = 0.98; // P(test-|no disease) = 98%, so P(test+|no disease) = 2%

const posterior = ProbabilityCalculator.bayesTheorem(
    diseasePrevalence, 
    testSensitivity, 
    1 - testSpecificity
);

console.log(\`If test is positive, probability of having disease: \${(posterior * 100).toFixed(1)}%\`);

console.log("\\n=== Monte Carlo Estimation ===");
// Estimate π using Monte Carlo method
const estimatePi = () => {
    const x = Math.random() * 2 - 1; // Random x between -1 and 1
    const y = Math.random() * 2 - 1; // Random y between -1 and 1
    return x * x + y * y <= 1; // Inside unit circle
};

const piEstimate = ProbabilityCalculator.monteCarloEstimate(estimatePi, 100000) * 4;
console.log(\`π estimate using Monte Carlo (100,000 trials): \${piEstimate.toFixed(4)}\`);
console.log(\`Actual π: \${Math.PI.toFixed(4)}\`);
console.log(\`Error: \${Math.abs(piEstimate - Math.PI).toFixed(4)}\`);`,
              hints: [
                'Classical probability: favorable outcomes / total outcomes',
                'Bayes theorem: P(A|B) = P(B|A) × P(A) / P(B)',
                'Binomial: n trials, each with probability p',
                'Monte Carlo uses random sampling for estimation',
                'Standard normal CDF can be approximated using error function'
              ]
            }
          ]
        }
      ]
    }
  ],
  assessments: [
    {
      id: 'statistics-midterm',
      title: 'Statistics Midterm Exam',
      type: 'exam',
      description: 'Comprehensive exam covering descriptive statistics, probability, and basic inference',
      questions: [
        {
          id: 'descriptive-stats',
          type: 'problem-solving',
          question: 'Given dataset [12, 15, 18, 20, 22, 25, 28, 30, 35, 40], calculate: (a) mean, median, mode, (b) range, variance, standard deviation, (c) quartiles and IQR, (d) identify any outliers using IQR method.',
          points: 25,
          rubric: 'Correct calculations (15 pts), proper formulas (5 pts), outlier identification (5 pts)'
        },
        {
          id: 'probability-calc',
          type: 'problem-solving',
          question: 'A medical test has 95% sensitivity and 98% specificity. If disease prevalence is 2%, what is the probability a person has the disease given a positive test? Use Bayes theorem and show all work.',
          points: 20,
          rubric: 'Bayes theorem setup (8 pts), correct calculations (8 pts), interpretation (4 pts)'
        },
        {
          id: 'distributions',
          type: 'problem-solving',
          question: 'For a binomial distribution with n=15 and p=0.3: (a) Find P(X=5), (b) Find expected value and variance, (c) What distribution would approximate this for large n?',
          points: 15,
          rubric: 'Binomial calculation (5 pts), expected value/variance (5 pts), normal approximation (5 pts)'
        }
      ],
      timeLimit: 90,
      passingScore: 70
    },
    {
      id: 'statistics-project',
      title: 'Statistical Analysis Project',
      type: 'project',
      description: 'Complete statistical analysis of real-world dataset with visualization and interpretation',
      requirements: [
        'Choose appropriate dataset with at least 100 observations',
        'Perform comprehensive descriptive statistical analysis',
        'Create meaningful data visualizations',
        'Apply probability concepts to data patterns',
        'Present findings with proper statistical interpretation',
        'Use statistical software for calculations and graphs'
      ],
      rubric: {
        'Data Analysis': 25,
        'Visualizations': 20,
        'Statistical Methods': 25,
        'Interpretation': 20,
        'Presentation': 10
      },
      timeLimit: null,
      passingScore: 75
    }
  ],
  resources: [
    {
      id: 'intro-statistics-textbook',
      title: 'Introduction to Statistical Learning',
      author: 'James, Witten, Hastie, Tibshirani',
      type: 'textbook',
      url: 'https://www.statlearning.com/',
      description: 'Comprehensive introduction to statistical methods and machine learning'
    },
    {
      id: 'khan-academy-stats',
      title: 'Khan Academy: Statistics and Probability',
      type: 'online-course',
      url: 'https://www.khanacademy.org/math/statistics-probability',
      description: 'Interactive lessons covering all fundamental statistics concepts'
    },
    {
      id: 'r-for-statistics',
      title: 'R for Data Science',
      type: 'online-resource',
      url: 'https://r4ds.had.co.nz/',
      description: 'Learn R programming for statistical analysis and data visualization'
    }
  ],
  courseFormat: 'hybrid',
  gradingScale: {
    'A': { min: 90, max: 100 },
    'B': { min: 80, max: 89 },
    'C': { min: 70, max: 79 },
    'D': { min: 60, max: 69 },
    'F': { min: 0, max: 59 }
  },
  policies: {
    attendance: 'Regular attendance required for statistical software training sessions. More than 2 absences may require additional assignments.',
    lateWork: 'Late assignments penalized 5% per day. Statistical projects have firm deadlines due to sequential nature.',
    academicIntegrity: 'Individual work required for exams. Collaboration allowed on homework and projects with proper attribution.',
    makeup: 'Makeup exams available for documented emergencies. Alternative statistical analysis projects may be assigned.',
    technology: 'Statistical software required (R, Python, or approved alternative). Course provides access to datasets and computing resources.',
    communication: 'Office hours focus on statistical concept clarification. Email response within 48 hours.',
    accommodations: 'Students requiring accommodations should work with Disability Services for appropriate statistical software and testing modifications.'
  }
};